import type { FunctionMeta } from '../function.model';

// =============================================================================
// PARAMS - Mathematical definition
// =============================================================================

/** Exponential function parameters: a * b^(x-h) + k */
export interface ExponentialParams {
  /** Vertical scale */
  a: number;
  /** Base (growth/decay factor, must be positive) */
  b: number;
  /** Horizontal shift */
  h: number;
  /** Vertical shift (horizontal asymptote) */
  k: number;
}

// =============================================================================
// META - Function configuration (type-checked)
// =============================================================================

/**
 * Configuration metadata for exponential functions.
 * Handle is placed at (h, a + k) since b^0 = 1.
 * This makes the handle visually sit on the curve.
 */
export const EXPONENTIAL_META: FunctionMeta<ExponentialParams> = {
  availableFeatures: [
    'handle',
    'roots',
    'tangent',
    'secant',
    'derivative',
    'asymptotes',
    'intersection',
    'yIntercept',
    'trajectory',
    'monotonicity',
    'functionArea',
    'intersectionArea',
  ],
  handleConfigs: {
    translate: {
      xParam: 'h',
      yParam: 'k',
      // Handle at (h, a + k) since b^0 = 1
      toPosition: (p) => [p['h'], p['a'] + p['k']],
      // When dragged to (x, y): h = x, k = y - a
      fromPosition: (x, y, p) => ({ h: x, k: y - p['a'] }),
    },
    'translate-x': {
      xParam: 'h',
      yParam: 'k',
      toPosition: (p) => [p['h'], p['a'] + p['k']],
      fromPosition: (x, _y, p) => ({ h: x, k: p['k'] }),
    },
    'translate-y': {
      xParam: 'h',
      yParam: 'k',
      toPosition: (p) => [p['h'], p['a'] + p['k']],
      fromPosition: (_x, y, p) => ({ h: p['h'], k: y - p['a'] }),
    },
  },
  defaultParams: {
    a: 1,
    b: 2,
    h: 0,
    k: 0,
  },
  defaultRanges: {
    a: [-5, 5],
    b: [0.1, 10],
    h: [-10, 10],
    k: [-10, 10],
  },
};
